import { Icon, SkeletonLoader, Text } from '@kanda-libs/ks-design-library';
import type { Job } from '@kanda-libs/ks-frontend-services';
import { AmountDisplay } from 'components/FinanceOptions/components/Options/Option/Rate/RateDetails/components';
import OpenBankingInfoPopover from './components/InfoPopover';
import useOpenBankingInfo from './useOpenBankingInfo';

export interface OpenBankingInfoProps {
  job?: Job;
  isLoading?: boolean;
}

const OpenBankingInfo: React.FunctionComponent<OpenBankingInfoProps> =
  function ({ job, isLoading = false }) {
    const { subsidy, example, showInvalid, isPaid } = useOpenBankingInfo(job);
    if (!job) return null;
    return (
      <div className="flex flex-row gap-x-4">
        {showInvalid ? (
          <>
            <Icon
              icon="close"
              stroke="red-200"
              className="my-auto min-w-5"
              isLoading={isLoading}
            />
            <Text
              className="text-12-18 text-neutral-600"
              text="Unfortunately, Open Banking is currently only available on jobs
              under £10,000"
            />
          </>
        ) : (
          <>
            <Text
              className="text-12-18 text-neutral-700 my-auto w-full"
              isLoading={isLoading}
              text={
                isPaid
                  ? 'Your customer has chosen to pay in full via Open Banking. You will be charged 1% of the total.'
                  : 'Your customer can choose to pay in full via Open Banking, charged at 1%, up to a maximum of £10,000.'
              }
              skeletonProps={{
                count: 3,
              }}
            />
            <SkeletonLoader
              wrapperClassName="w-5 min-w-5 h-5 my-auto"
              isLoading={isLoading}
              afterLoading={
                <OpenBankingInfoPopover>
                  <div className="grid grid-cols-[1fr_auto] gap-y-2 gap-x-2">
                    <p className="text-18-22-em md:text-12-18-em text-neutral-900 col-span-2 mb-2 md:mb-0">
                      Cost Breakdown
                    </p>
                    <div className="border-b border-neutral-200 col-span-2" />
                    <AmountDisplay
                      title="Subsidy (1% of the job value)"
                      amount={subsidy}
                    />
                    <div className="border-b border-neutral-200 col-span-2" />
                    <p className="text-18-22-em md:text-12-18-em text-neutral-900 col-span-2 mb-2 md:mb-0 mt-2">
                      Card Payment Comparison
                    </p>
                    <div className="border-b border-neutral-200 col-span-2" />
                    <AmountDisplay
                      title="Example card fee (1.75%)"
                      amount={example}
                    />
                    <div className="border-b border-neutral-200 col-span-2" />
                    <AmountDisplay
                      title="Savings to you!"
                      amount={example - subsidy}
                      emphasized
                    />
                  </div>
                </OpenBankingInfoPopover>
              }
            />
          </>
        )}
      </div>
    );
  };

export default OpenBankingInfo;
